/**
 * Skeleton Page Template Component
 *
 * Purpose: Provides a standardized layout for pages in the OrchestrateHQ AI platform,
 *          incorporating TopBanner and LeftSidebar for consistent navigation and structure
 *
 * Architecture: Combines TopBanner and LeftSidebar components with flexible content area
 *
 * Dependencies: React, Material-UI, TopBanner, LeftSidebar
 * Author: Development Team
 * Created: 2025-08-07
 */

import React from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import TopBanner from './TopBanner';
import LeftSidebar from './LeftSidebar';
import { useSidebar } from '../../context/SidebarContext';

// Layout constants - Must match LeftSidebar dimensions exactly
const SIDEBAR_WIDTH_EXPANDED = 240;
const SIDEBAR_WIDTH_COLLAPSED = 72;
const TOP_BANNER_HEIGHT = 64; // Matches TopBanner toolbar height

// Styled components
const SkeletonRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  position: 'relative',
  width: '100%',
}));
const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  overflow: 'auto',
}));
interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

interface SkeletonPageProps {
  /** Page title to display in TopBanner */
  pageTitle: string;
  /** Main content to display in the page */
  children: React.ReactNode;
  /** Whether to add padding to content area (default: true) */
  padded?: boolean;
  /** Additional styles for the content area */
  contentSx?: object;
  /** Optional breadcrumbs to display in TopBanner */
  breadcrumbs?: BreadcrumbItem[];
  /** Whether to show search bar in TopBanner (default: false) */
  showSearch?: boolean;
}

/**
 * Skeleton Page Template Component
 *
 * Purpose: Provides consistent layout structure with navigation and content area
 * Logic: Combines TopBanner and LeftSidebar with responsive behavior and content area
 * Input: pageTitle - Title to display in header
 *        children - Main page content
 *        padded - Whether to add padding to content
 *        contentSx - Additional styles for content area
 *        breadcrumbs - Optional breadcrumbs for navigation
 *        showSearch - Whether to show search bar in TopBanner
 * Output: JSX element with complete page layout
 *
 * @param pageTitle - Page title for TopBanner
 * @param children - Main page content
 * @param padded - Whether to add default padding to content area
 * @param contentSx - Additional styles for content area
 * @param breadcrumbs - Optional breadcrumbs for navigation
 * @param showSearch - Whether to show search bar in TopBanner
 * @returns JSX element with skeleton page layout
 */
const SkeletonPage: React.FC<SkeletonPageProps> = ({
  pageTitle,
  children,
  padded = true,
  contentSx = {},
  breadcrumbs,
  showSearch = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Use global sidebar state from context (persists across navigations)
  const { sidebarOpen, setSidebarOpen, toggleSidebar } = useSidebar();

  // Auto-collapse sidebar on mobile (only collapse, never auto-expand)
  React.useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile, setSidebarOpen]);

  return (
    <SkeletonRoot>
      {/* Top Banner - Fixed Header */}
      <Box
        component="header"
        sx={{
          position: 'absolute',
          top: 0,
          left: isMobile ? 0 : sidebarOpen ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED,
          right: 0,
          zIndex: theme.zIndex.drawer + 1,
          transition: theme.transitions.create('left', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <TopBanner pageTitle={pageTitle} breadcrumbs={breadcrumbs} showSearch={showSearch} />
      </Box>

      {/* Left Sidebar - Navigation */}
      <LeftSidebar open={sidebarOpen} onToggle={toggleSidebar} />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flex: 1,
          padding: 0,
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginTop: `${TOP_BANNER_HEIGHT}px`,
          minHeight: `calc(100vh - ${TOP_BANNER_HEIGHT}px)`,
          overflow: 'auto',
        }}
      >
        {padded ? (
          <ContentArea sx={contentSx}>{children}</ContentArea>
        ) : (
          <Box sx={{ height: '100%', ...contentSx }}>{children}</Box>
        )}
      </Box>
    </SkeletonRoot>
  );
};

export default SkeletonPage;
